import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooks } from '../context/BooksContext';
import { useUser } from '../context/UserContext';
import { IoAdd, IoEye, IoHeart, IoBook, IoCreate, IoGlobe, IoLockClosed, IoStar, IoTime, IoTrendingUp, IoChevronDown, IoChevronUp, IoStatsChart, IoTrash } from 'react-icons/io5';
import AuthorChart from '../components/AuthorChart';
import ExportBook from '../components/ExportBook';
import { estimateBookReadingTime, formatReadingTime } from '../utils/readingTime';
import './AuthorDashboard.css';

function getAverageRating(book) {
  const ratings = book.ratings ? Object.values(book.ratings) : [];
  if (ratings.length === 0) return null;
  return (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(1);
}

function getRatingCount(book) {
  return book.ratings ? Object.keys(book.ratings).length : 0;
}

export default function AuthorDashboard() {
  const navigate = useNavigate();
const { books, publishBook, unpublishBook, deleteBook } = useBooks();
  const { user } = useUser();
  const [filter, setFilter] = useState('all');
  const [expandedBook, setExpandedBook] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showPerformance, setShowPerformance] = useState(false);


  const myBooks = books.filter(b => b.authorId === user.authorId);
  const publishedBooks = myBooks.filter(b => b.status === 'published' || !b.status);
  const draftBooks = myBooks.filter(b => b.status === 'draft');
  const totalLikes = myBooks.reduce((sum, b) => sum + (b.likes || 0), 0);
  const totalViews = myBooks.reduce((sum, b) => sum + (b.views || 0), 0);
  const totalChapters = myBooks.reduce((sum, b) => sum + (b.chapters || []).length, 0);
  const totalWords = myBooks.reduce((sum, b) => {
    return sum + (b.chapters || []).reduce((s, ch) => s + ((ch.content || '').split(/\s+/).length), 0);
  }, 0);

  const displayBooks = filter === 'published' ? publishedBooks
    : filter === 'drafts' ? draftBooks : myBooks;

  return (
    <div className="page author-page">
      <div className="author-header">
        <div>
          <h1 className="author-title">Mes œuvres</h1>
          <p className="author-count">{myBooks.length} livre{myBooks.length > 1 ? 's' : ''} · {totalChapters} chapitres · {totalWords.toLocaleString('fr-FR')} mots</p>
        </div>
        <motion.button
          className="author-new-btn"
          onClick={() => navigate('/author/write')}
          whileTap={{ scale: 0.95 }}
        >
          <IoAdd /> Nouveau
        </motion.button>
      </div>

      {/* Stats globales */}
      <div className="author-stats-row">
        <div className="author-stat-card">
          <IoBook style={{ fontSize: 20, color: 'var(--amber)' }} />
          <span className="author-stat-value">{publishedBooks.length}</span>
          <span className="author-stat-label">Publiés</span>
        </div>
        <div className="author-stat-card">
          <IoCreate style={{ fontSize: 20, color: 'var(--plum)' }} />
          <span className="author-stat-value">{draftBooks.length}</span>
          <span className="author-stat-label">Brouillons</span>
        </div>
        <div className="author-stat-card">
          <IoHeart style={{ fontSize: 20, color: 'var(--rose)' }} />
          <span className="author-stat-value">{totalLikes.toLocaleString('fr-FR')}</span>
          <span className="author-stat-label">Likes</span>
        </div>
        <div className="author-stat-card">
          <IoEye style={{ fontSize: 20, color: 'var(--sky)' }} />
          <span className="author-stat-value">{totalViews.toLocaleString('fr-FR')}</span>
          <span className="author-stat-label">Vues</span>
        </div>
      </div>

{/* Performance des livres — collapsible */}
{myBooks.length > 0 && (
  <div className="author-performance-section">
    <button
      className="author-performance-toggle"
      onClick={() => setShowPerformance(!showPerformance)}
    >
      <span>📊 Performance des livres</span>
      {showPerformance ? <IoChevronUp /> : <IoChevronDown />}
    </button>
    <AnimatePresence>
      {showPerformance && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ overflow: 'hidden' }}
        >
          <AuthorChart books={myBooks} />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)}

      {/* Filtres */}
      <div className="author-filters">
        {[
          { id: 'all', label: 'Tous (' + myBooks.length + ')' },
          { id: 'published', label: 'Publiés (' + publishedBooks.length + ')' },
          { id: 'drafts', label: 'Brouillons (' + draftBooks.length + ')' },
        ].map(f => (
          <button
            key={f.id}
            className={'author-filter-btn ' + (filter === f.id ? 'author-filter-active' : '')}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste des livres */}
      <div className="author-books-list">
        {displayBooks.length > 0 ? (
          displayBooks.map((book, i) => {
            const isDraft = book.status === 'draft';
            const avgRating = getAverageRating(book);
            const ratingCount = getRatingCount(book);
            const chapters = book.chapters || [];
            const wordCount = chapters.reduce((s, ch) => s + ((ch.content || '').split(/\s+/).length), 0);
            const readTime = estimateBookReadingTime(chapters);
            const isExpanded = expandedBook === book.id;

            return (
              <motion.div
                key={book.id}
                className="author-book-card"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <div className="author-book-cover" style={{
                  background: book.coverImage ? `url(${book.coverImage}) center/cover no-repeat` : book.coverGradient
                }}>
                  <span className={'author-book-status ' + (isDraft ? 'author-book-draft' : 'author-book-published')}>
                    {isDraft ? <><IoLockClosed /> Brouillon</> : <><IoGlobe /> Publié</>}
                  </span>
                </div>

                <div className="author-book-info">
                  <h3 className="author-book-title">{book.title}</h3>
                  <p className="author-book-meta">
                    {chapters.length} chap. · {(book.likes || 0)} ❤️ · {(book.views || 0)} 👁
                    {avgRating && <> · ⭐ {avgRating} ({ratingCount})</>}
                  </p>

                  {/* Bouton stats détaillées */}
                  <button className="author-book-stats-toggle" onClick={() => setExpandedBook(isExpanded ? null : book.id)}>
                    <IoStatsChart /> Stats détaillées {isExpanded ? <IoChevronUp /> : <IoChevronDown />}
                    {!user?.premium && <span className="author-premium-badge">Premium</span>}
                  </button>

                  <AnimatePresence>
                    {isExpanded && user?.premium && (
                      <motion.div
                        className="author-book-details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="author-book-detail-grid">
                          <div className="author-detail-item">
                            <IoBook className="author-detail-icon" />
                            <div>
                              <strong>{chapters.length}</strong>
                              <span>Chapitres</span>
                            </div>
                          </div>
                          <div className="author-detail-item">
                            <IoCreate className="author-detail-icon" />
                            <div>
                              <strong>{wordCount.toLocaleString('fr-FR')}</strong>
                              <span>Mots</span>
                            </div>
                          </div>
                          <div className="author-detail-item">
                            <IoTime className="author-detail-icon" />
                            <div>
                              <strong>{formatReadingTime(readTime)}</strong>
                              <span>Temps de lecture</span>
                            </div>
                          </div>
                          <div className="author-detail-item">
                            <IoTrendingUp className="author-detail-icon" />
                            <div>
                              <strong>{(book.views || 0) > 0 ? ((book.likes || 0) / (book.views || 1) * 100).toFixed(1) + '%' : '—'}</strong>
                              <span>Taux engagement</span>
                            </div>
                          </div>
                          <div className="author-detail-item">
                            <IoStar className="author-detail-icon" />
                            <div>
                              <strong>{avgRating || '—'}</strong>
                              <span>Note ({ratingCount} votes)</span>
                            </div>
                          </div>
                          <div className="author-detail-item">
                            <IoHeart className="author-detail-icon" />
                            <div>
                              <strong>{(book.likes || 0).toLocaleString('fr-FR')}</strong>
                              <span>Likes total</span>
                            </div>
                          </div>
                        </div>

                        {/* Stats par chapitre */}
                        {chapters.length > 0 && (
                          <div className="author-chapter-stats">
                            <p className="author-chapter-stats-title">Détail par chapitre</p>
                            {chapters.map((ch, ci) => {
                              const chWords = (ch.content || '').split(/\s+/).length;
                              return (
                                <div key={ci} className="author-chapter-stat-row">
                                  <span className="author-chapter-stat-num">{ci + 1}</span>
                                  <span className="author-chapter-stat-name">{ch.title || 'Sans titre'}</span>
                                  <span className="author-chapter-stat-words">{chWords} mots</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                    {isExpanded && !user?.premium && (
                    <div className="author-premium-wall">
                      <p className="author-premium-wall-emoji">🔒</p>
                      <p className="author-premium-wall-text">Les stats détaillées sont réservées aux membres Premium</p>
                      <button className="btn btn-primary" style={{ fontSize: 13, padding: '8px 16px', marginTop: 8 }} onClick={() => navigate('/profile')}>
                        Découvrir Premium
                      </button>
                    </div>
                  )}

                  <div className="author-book-actions">
                    <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => navigate('/author/write/' + book.id + '/chapter')}>
                      + Chapitre
                    </button>
                    <button className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => navigate('/book/' + book.id)}>
                      Voir
                    </button>
                    <ExportBook book={book} />
                    {isDraft ? (
                      <button className="btn btn-accent" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => publishBook(book.id)}>
                        Publier
                      </button>
                    ) : (
                      <button className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => unpublishBook(book.id)}>
                        Dépublier
                      </button>
                    )}
                    <button className="btn btn-ghost author-delete-btn" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => setConfirmDelete(book.id)}>
                      <IoTrash /> Supprimer
                    </button>
                  </div>

                  {/* Modal confirmation suppression */}
                  <AnimatePresence>
                    {confirmDelete === book.id && (
                      <motion.div
                        className="author-delete-confirm"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                      >
                        <p>Supprimer <strong>« {book.title} »</strong> définitivement ?</p>
                        <div className="author-delete-confirm-actions">
                          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setConfirmDelete(null)}>Annuler</button>
                          <button className="btn author-delete-confirm-btn" style={{ fontSize: 12 }} onClick={() => { deleteBook(book.id); setConfirmDelete(null); }}>Oui, supprimer</button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">✍️</div>
            <p className="empty-state-text">
              {filter === 'drafts' ? "Aucun brouillon" : filter === 'published' ? "Aucun livre publié" : "Écris ton premier livre !"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}